import { useEffect, useRef, useState } from "react";
import "@/App.css";
import { BrowserRouter, Link, Route, Routes, useLocation, useParams } from "react-router-dom";
import Lenis from "lenis";
import { motion, useScroll, useTransform } from "framer-motion";
import { categories, images, projects, projectsByCategory } from "@/data/projects";

const EASE = [0.22, 1, 0.36, 1];

function Reveal({ children, delay = 0, className = "" }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 42 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-70px" }}
      transition={{ duration: 1, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  );
}

function Logo({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden="true" className="logo-mark">
      <path d="M7 26 16 5l9 21M11.2 18.5h9.6" stroke="#A68B6A" strokeWidth="2.4" fill="none" />
    </svg>
  );
}

function SmoothScroll() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    let frame;
    const loop = (time) => { lenis.raf(time); frame = requestAnimationFrame(loop); };
    frame = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(frame); lenis.destroy(); };
  }, []);
  return null;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => window.scrollTo({ top: 0, behavior: "instant" }), [pathname]);
  return null;
}

function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [menu, setMenu] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 32);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => setMenu(false), [pathname]);
  const onDark = pathname === "/" && !scrolled;
  return (
    <header className={`site-nav ${onDark ? "on-dark" : ""} ${scrolled ? "is-scrolled" : ""}`} data-testid="site-navigation">
      <Link to="/" className="wordmark" data-testid="home-link">
        <Logo />
        <span className="wordmark-text">ANAPL</span>
      </Link>
      <button className="menu-toggle" onClick={() => setMenu(!menu)} data-testid="mobile-menu-button">{menu ? "Close" : "Menu"}</button>
      <nav className={menu ? "nav-links is-open" : "nav-links"} data-testid="primary-navigation">
        <Link to="/" data-testid="nav-home-link">Home</Link>
        <Link to="/about" data-testid="about-link">About</Link>
        <Link to="/work" data-testid="work-link">Work</Link>
        <Link to="/contact" className="nav-contact" data-testid="contact-link">Start a conversation</Link>
      </nav>
    </header>
  );
}

function SectionIntro({ number, eyebrow, title, children }) {
  return (
    <Reveal className="section-intro">
      <span className="eyebrow">{number} / {eyebrow}</span>
      <h2>{title}</h2>
      {children && <div className="intro-copy">{children}</div>}
    </Reveal>
  );
}

function Footer() {
  return (
    <footer className="footer" data-testid="site-footer">
      <div><span className="eyebrow">ANAPL / 2016—2026</span><p className="footer-line">Spaces with a point of view.</p></div>
      <div className="footer-right"><span>Mumbai · Pune · Gurugram · Gujarat</span><a href="mailto:studio@anapl.in" data-testid="footer-email-link">studio@anapl.in</a></div>
    </footer>
  );
}

function Marquee() {
  const items = ["Architectural Design", "Interior Design", "Turnkey Solutions", "Mumbai · Est. 2016"];
  const row = [...items, ...items, ...items];
  return (
    <div className="marquee" aria-hidden="true" data-testid="editorial-marquee">
      <div className="marquee-track">
        {[0, 1].map((half) => (
          <div className="marquee-half" key={half}>
            {row.map((item, i) => <span key={i}>{item}<i>·</i></span>)}
          </div>
        ))}
      </div>
    </div>
  );
}

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.02, 1.14]);
  const line = { hidden: { y: "115%" }, show: (i) => ({ y: "0%", transition: { duration: 1.15, delay: 0.35 + i * 0.14, ease: EASE } }) };
  return (
    <section className="hero" ref={ref} data-testid="hero-section">
      <motion.div className="hero-bg" style={{ backgroundImage: `url(${images.hero})`, y, scale }} />
      <div className="hero-overlay" />
      <div className="hero-content">
        <motion.span className="eyebrow hero-label" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 0.9 }}>Mumbai · Est. 2016</motion.span>
        <h1>
          <span className="mask-line"><motion.span variants={line} custom={0} initial="hidden" animate="show">Elevating Spaces,</motion.span></span>
          <span className="mask-line"><motion.span variants={line} custom={1} initial="hidden" animate="show"><em>Defining Lifestyles.</em></motion.span></span>
        </h1>
        <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.95, duration: 0.9, ease: EASE }}>
          <Link className="text-link light-link" to="/contact" data-testid="begin-project-link">Begin a project</Link>
        </motion.div>
      </div>
      <span className="hero-index">01—04</span>
    </section>
  );
}

function Home() {
  const featured = [projects[0], projects[4], projects[8], projects[2]];
  return (
    <>
      <main>
        <Hero />
        <section className="manifesto page-pad">
          <SectionIntro number="01" eyebrow="The practice" title={<>A quieter kind of<br /><em>confidence.</em></>}>
            <p>Crafting luxury spaces with timeless design, innovation, and unmatched detail. We build places that hold their value — materially, emotionally, and over time.</p>
          </SectionIntro>
          <Reveal className="stats" delay={0.1}>
            <div><strong>10</strong><span>years of practice</span></div>
            <div><strong>100<span>+</span></strong><span>professionals</span></div>
            <div><strong>100<span>+</span></strong><span>projects</span></div>
            <div><strong>9.1 <small>/ 10</small></strong><span>client rating</span></div>
          </Reveal>
        </section>
        <section className="services page-pad">
          <SectionIntro number="02" eyebrow="What we do" title={<>From first line<br /><em>to final detail.</em></>} />
          <div className="service-list">
            {categories.map((cat) => (
              <Link to={`/work/${cat.id}`} className="service-row" key={cat.id} data-testid={`service-row-${cat.id}`}>
                <span className="service-no">{cat.number}</span>
                <h3>{cat.title}</h3>
                <span className="service-desc">{cat.id === "architectural-design" ? "Concept to completion" : cat.id === "interior-design" ? "Precision in every layer" : "Premium craftsmanship"}</span>
              </Link>
            ))}
          </div>
          <div className="service-galleries">
            {categories.map((gallery) => (
              <section className="service-gallery" key={gallery.id} data-testid={`${gallery.id}-gallery`}>
                <Reveal className="service-gallery-heading">
                  <span className="eyebrow">{gallery.number} / Service study</span>
                  <div>
                    <h3>{gallery.title}</h3>
                    <p>{gallery.short}</p>
                    <Link className="text-link" to={`/work/${gallery.id}`} data-testid={`${gallery.id}-know-more`}>Know more</Link>
                  </div>
                </Reveal>
                <div className={`service-gallery-grid ${gallery.id}`}>
                  {projectsByCategory(gallery.id).map((item, i) => (
                    <Reveal key={item.slug} delay={(i % 2) * 0.08}>
                      <Link to={`/project/${item.slug}`} className="service-gallery-item" data-testid={`${gallery.id}-${item.slug}`}>
                        <div className="image-wrap portrait"><img src={item.image} alt={item.title} loading="lazy" /></div>
                        <div className="service-gallery-meta"><h4>{item.title}</h4><span>{item.city} · {item.type}</span></div>
                      </Link>
                    </Reveal>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </section>
        <section className="work-preview page-pad">
          <SectionIntro number="03" eyebrow="Selected work" title={<>Made for the<br /><em>long view.</em></>} />
          <div className="project-grid">
            {featured.map((project, i) => (
              <Reveal key={project.slug} delay={(i % 2) * 0.08} className={`project-slot slot-${i + 1}`}>
                <Link to={`/project/${project.slug}`} className="project-card" data-testid={`project-card-${project.slug}`}>
                  <div className="image-wrap portrait"><img src={project.image} alt={project.title} loading="lazy" /></div>
                  <div className="project-meta"><h3>{project.title}</h3><span>{project.city}</span></div>
                </Link>
              </Reveal>
            ))}
          </div>
          <Link to="/work" className="text-link work-link" data-testid="view-all-work-link">Know more</Link>
        </section>
        <section className="quote-block page-pad">
          <Reveal>
            <span className="quote-mark">“</span>
            <blockquote>They listened to what we could not articulate and translated it into a space that feels entirely ours.</blockquote>
            <cite>— Anurag Mishra, client</cite>
          </Reveal>
        </section>
        <Marquee />
        <section className="closing page-pad">
          <Reveal>
            <span className="eyebrow">04 / The next line</span>
            <h2>Let’s design<br /><em>your space.</em></h2>
            <Link to="/contact" className="text-link" data-testid="closing-contact-link">Know more</Link>
          </Reveal>
        </section>
      </main>
      <Footer />
    </>
  );
}

function About() {
  return (
    <>
      <main className="inner-page">
        <section className="page-heading page-pad"><span className="eyebrow">01 / About the studio</span><h1>Built from<br /><em>the ground up.</em></h1><p>Architect C.N Veer &amp; Associates is a multidisciplinary practice shaping enduring spaces across India and beyond.</p></section>
        <Reveal className="about-image page-pad"><div className="image-wrap about-frame"><img src={images.about} alt="Hand-finished stone and warm material detail" /></div></Reveal>
        <section className="about-story page-pad">
          <div><span className="eyebrow">A practice in progress</span></div>
          <div className="story-copy">
            <p className="large-copy">Founded in 2016 as a two-person practice, ANAPL has grown through a simple commitment: design excellence, flawless execution, and personalised service.</p>
            <div className="story-columns">
              <p><strong>Our mission</strong><br />To create considered architecture and interiors that carry the intelligence of their brief into every detail.</p>
              <p><strong>Our vision</strong><br />Spaces that embody luxury, intelligence, and timeless appeal — without asking for attention.</p>
            </div>
          </div>
        </section>
        <section className="belief page-pad"><span className="eyebrow">A working belief</span><blockquote>We believe interiors are an asset; they create value, build brand presence, and influence perceptions.</blockquote></section>
        <section className="team page-pad">
          <SectionIntro number="02" eyebrow="The people" title="Two points of view." />
          <div className="team-grid">
            <div className="portrait"><div className="portrait-block">CNV</div><h3>C.N Veer</h3><span>Lead Architect</span></div>
            <div className="portrait"><div className="portrait-block portrait-alt">NV</div><h3>Nisha Veer</h3><span>Director</span></div>
          </div>
          <p className="climate-note"><span className="eyebrow">In practice</span> Our work is attentive to climate, orientation and the long life of materials. Sustainable and climate-responsive thinking is present from the first sketch, not added at the end.</p>
        </section>
      </main>
      <Footer />
    </>
  );
}

function Work() {
  return (
    <>
      <main className="inner-page">
        <section className="page-heading page-pad"><span className="eyebrow">02 / The archive</span><h1>A record of<br /><em>making.</em></h1><p>Three disciplines, one standard. Open a field of work to see its portfolio.</p></section>
        <section className="work-index page-pad" data-testid="work-category-index">
          {categories.map((cat, i) => {
            const works = projectsByCategory(cat.id);
            return (
              <Reveal className="work-category" key={cat.id} delay={i * 0.06}>
                <div className="work-category-head">
                  <span className="eyebrow">{cat.number} / {works.length} projects</span>
                  <Link to={`/work/${cat.id}`} data-testid={`work-category-${cat.id}`}><h2>{cat.title}</h2></Link>
                  <p>{cat.description}</p>
                  <Link className="text-link" to={`/work/${cat.id}`} data-testid={`work-category-${cat.id}-know-more`}>Know more</Link>
                </div>
                <div className="work-category-strip">
                  {works.slice(0, 3).map((p) => (
                    <Link to={`/project/${p.slug}`} className="work-thumb" key={p.slug} data-testid={`work-thumb-${p.slug}`}>
                      <div className="image-wrap portrait"><img src={p.image} alt={p.title} loading="lazy" /></div>
                      <span>{p.title}</span>
                    </Link>
                  ))}
                </div>
              </Reveal>
            );
          })}
        </section>
      </main>
      <Footer />
    </>
  );
}

function Category() {
  const { categoryId } = useParams();
  const category = categories.find((c) => c.id === categoryId) || categories[0];
  const works = projectsByCategory(category.id);
  const [first, ...rest] = category.title.split(" ");
  return (
    <>
      <main className="inner-page">
        <section className="page-heading page-pad" data-testid={`category-heading-${category.id}`}>
          <span className="eyebrow">{category.number} / {works.length} projects</span>
          <h1>{first}<br /><em>{rest.join(" ")}.</em></h1>
          <p>{category.description}</p>
        </section>
        <section className="category-grid page-pad" data-testid={`category-grid-${category.id}`}>
          {works.map((p, i) => (
            <Reveal key={p.slug} delay={(i % 2) * 0.08} className={`category-slot slot-${(i % 2) + 1}`}>
              <Link to={`/project/${p.slug}`} className="category-item" data-testid={`category-project-${p.slug}`}>
                <div className="image-wrap portrait"><img src={p.image} alt={p.title} /></div>
                <div className="project-meta"><h3>{p.title}</h3><span>{p.city} · {p.year}</span></div>
                <p className="category-item-text">{p.text}</p>
              </Link>
            </Reveal>
          ))}
        </section>
        <section className="category-nav page-pad">
          <span className="eyebrow">Continue to</span>
          {categories.filter((c) => c.id !== category.id).map((c) => (
            <Link key={c.id} to={`/work/${c.id}`} className="text-link" data-testid={`category-nav-${c.id}`}>{c.title}</Link>
          ))}
        </section>
      </main>
      <Footer />
    </>
  );
}

function Project() {
  const { slug } = useParams();
  const project = projects.find((item) => item.slug === slug) || projects[0];
  const category = categories.find((c) => c.id === project.category);
  return (
    <>
      <main className="project-page inner-page">
        <section className="project-head page-pad">
          <div className="project-head-copy">
            <span className="eyebrow">{category.title} / {project.year}</span>
            <h1>{project.title}</h1>
            <span className="project-city">{project.city}</span>
            <p className="project-lead">{project.text} The architecture is composed as a sequence of thresholds, balancing presence with retreat and allowing material to do the talking.</p>
            <div className="project-facts"><p>{project.type}<br />{project.city}<br />{project.year}</p></div>
            <Link className="text-link" to={`/work/${category.id}`} data-testid="project-category-link">More {category.title}</Link>
          </div>
          <Reveal className="project-portrait">
            <div className="image-wrap portrait"><img src={project.image} alt={project.title} /></div>
          </Reveal>
        </section>
        <section className="project-story page-pad">
          <div className="project-facts"><span className="eyebrow">The brief</span><p>{project.type}<br />{project.city}<br />{project.year}</p></div>
          <div>
            <div className="story-columns">
              <p><strong>Material note</strong><br />Hand-finished stone, smoked oak and brushed metal bring a quiet warmth to the shell.</p>
              <p><strong>Approach</strong><br />Light is treated as a material — measured, reflected and allowed to change through the day.</p>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}

function Contact() {
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", type: "", message: "" });
  const submit = async (event) => {
    event.preventDefault();
    setError(false);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/inquiries`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form) });
      if (!response.ok) throw new Error(`Inquiry request failed: ${response.status}`);
      setSent(true);
    } catch (requestError) {
      console.error(requestError);
      setError(true);
    }
  };
  return (
    <>
      <main className="inner-page contact-page">
        <section className="page-heading page-pad"><span className="eyebrow">03 / Start a conversation</span><h1>From concept<br /><em>to creation.</em></h1><p>Tell us what you are imagining. We bring the right people, questions and detail to the first conversation.</p></section>
        <section className="contact-layout page-pad">
          <div className="contact-details">
            <span className="eyebrow">Mumbai studio</span>
            <p>Architect C.N Veer &amp; Associates<br />Mumbai, Maharashtra<br /><a href="tel:+917208000078" data-testid="phone-link">+91 7208000078</a></p>
            <span className="eyebrow">Other studios</span>
            <p>Pune · Gurugram · Gujarat</p>
          </div>
          <form onSubmit={submit} className="inquiry-form" data-testid="inquiry-form">
            {[["name", "Your name", "text"], ["phone", "Phone number", "tel"], ["email", "Email address", "email"], ["type", "Project type", "text"]].map(([id, label, type]) => (
              <label key={id}>{label}<input required={id !== "phone"} type={type} value={form[id]} onChange={(e) => setForm({ ...form, [id]: e.target.value })} data-testid={`inquiry-${id}-input`} /></label>
            ))}
            <label>Tell us about the project<textarea required rows="4" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="inquiry-message-input" /></label>
            {sent ? <p className="success-note" data-testid="inquiry-success-message">Thank you. We’ll be in touch soon.</p> : (
              <>
                <button type="submit" className="text-link form-submit" data-testid="inquiry-submit-button">Send inquiry <span>↗</span></button>
                {error && <p className="error-note" data-testid="inquiry-error-message">We could not send this inquiry. Please try again.</p>}
              </>
            )}
          </form>
        </section>
      </main>
      <Footer />
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <SmoothScroll />
      <ScrollToTop />
      <Nav />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/work" element={<Work />} />
        <Route path="/work/:categoryId" element={<Category />} />
        <Route path="/project/:slug" element={<Project />} />
        <Route path="/contact" element={<Contact />} />
      </Routes>
    </BrowserRouter>
  );
}
export default App;
